import React, { useEffect, useState, useRef } from 'react';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

const StarRating = ({ value, onChange, label }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>{label}</div>
    <div style={{ display: 'flex', gap: 8 }}>
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          style={{
            fontSize: 28,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px',
            opacity: star <= value ? 1 : 0.25,
            transition: 'opacity 0.15s, transform 0.1s',
            transform: star <= value ? 'scale(1.1)' : 'scale(1)',
          }}
        >⭐</button>
      ))}
    </div>
  </div>
);

const StatBar = ({ label, value, max = 5 }) => {
  const pct = ((value || 0) / max) * 100;
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="row-between" style={{ marginBottom: 5 }}>
        <span style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>{value ? parseFloat(value).toFixed(1) : '—'}</span>
      </div>
      <div style={{ height: 8, background: 'var(--bg3)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'var(--accent)', borderRadius: 4, transition: 'width 0.5s ease' }} />
      </div>
    </div>
  );
};

export default function Profile() {
  const { user, login } = useAuth();
  const [ratings, setRatings] = useState(null);
  const [pendingSessions, setPendingSessions] = useState([]);
  const [ratingModal, setRatingModal] = useState(null); // session to rate
  const [ratingForm, setRatingForm] = useState({ errorsRating: 0, defenseRating: 0, offenseRating: 0, sessionEnjoyment: 0 });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileRef = useRef();

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const loadData = async () => {
    try {
      const [ratingsRes, sessionsRes] = await Promise.all([
        API.get('/ratings/me'),
        API.get('/sessions'),
      ]);
      setRatings(ratingsRes.data);

      // Find sessions I was in that are played/settled but I haven't rated
      const ratedSessionIds = new Set(ratingsRes.data.history.map(r => r.session_id));
      const pending = sessionsRes.data.filter(s =>
        ['played', 'settled'].includes(s.status) && !ratedSessionIds.has(s.id)
      );
      setPendingSessions(pending.slice(0, 5));
    } catch (err) { console.error(err); }
  };

  useEffect(() => { loadData(); }, []);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500 * 1024) return setError('Photo must be under 500KB');

    setUploading(true);
    setError('');
    try {
      // Upload directly to Cloudinary from browser using FormData (no base64 issues)
      const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
      const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', uploadPreset);

      const cloudRes = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        method: 'POST',
        body: formData,
      });

      const cloudData = await cloudRes.json();
      if (!cloudData.secure_url) throw new Error('Cloudinary upload failed');

      // Save URL to our backend
      await API.post('/upload/photo', { photoUrl: cloudData.secure_url });

      const updatedUser = { ...user, photoUrl: cloudData.secure_url };
      login(localStorage.getItem('pt_token'), updatedUser);
      flash('Photo updated! 🎉');
    } catch (err) {
      setError('Upload failed. Please try again.');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const openRatingModal = async (session) => {
    setRatingModal(session);
    setRatingForm({ errorsRating: 0, defenseRating: 0, offenseRating: 0, sessionEnjoyment: 0 });
    try {
      const existing = await API.get(`/ratings/session/${session.id}/me`);
      if (existing.data) {
        setRatingForm({
          errorsRating: existing.data.errors_rating || 0,
          defenseRating: existing.data.defense_rating || 0,
          offenseRating: existing.data.offense_rating || 0,
          sessionEnjoyment: existing.data.session_enjoyment || 0,
        });
      }
    } catch {}
  };

  const submitRating = async () => {
    setSubmitting(true);
    try {
      await API.post(`/ratings/session/${ratingModal.id}`, ratingForm);
      setRatingModal(null);
      flash('Rating saved!');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving rating');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-shell">
      <div className="topbar">
        <div className="topbar-title">My Profile</div>
      </div>
      <div className="page fade-in">
        {error && <div className="error-msg">{error}</div>}
        {success && <div style={{ background: 'var(--success-light)', border: '1px solid #bbf7d0', color: 'var(--success)', padding: '11px 14px', borderRadius: 10, marginBottom: 14, fontSize: 14, fontWeight: 500 }}>{success}</div>}

        {/* Profile card */}
        <div className="card" style={{ textAlign: 'center', padding: '28px 20px', marginBottom: 16 }}>
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: 16 }}>
            {user.photoUrl ? (
              <img src={user.photoUrl} alt={user.name}
                style={{ width: 90, height: 90, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--border)' }} />
            ) : (
              <div style={{ width: 90, height: 90, borderRadius: '50%', background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, fontWeight: 700, color: 'var(--accent)', fontFamily: 'Plus Jakarta Sans', border: '3px solid var(--border)', margin: '0 auto' }}>
                {user.name[0].toUpperCase()}
              </div>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              style={{ position: 'absolute', bottom: 0, right: 0, width: 28, height: 28, borderRadius: '50%', background: 'var(--accent)', border: '2px solid white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}
            >📷</button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
          <div style={{ fontFamily: 'Plus Jakarta Sans', fontSize: 20, fontWeight: 700 }}>{user.name}</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>{user.phone}</div>
          {uploading && <div style={{ fontSize: 13, color: 'var(--accent)', marginTop: 8 }}>Uploading photo...</div>}
        </div>

        {/* My stats */}
        {ratings && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="section-title" style={{ marginBottom: 16 }}>My Performance Averages</div>
            <StatBar label="Unforced Errors (lower = better)" value={ratings.averages?.rating_errors} />
            <StatBar label="Defense" value={ratings.averages?.rating_defense} />
            <StatBar label="Offense" value={ratings.averages?.rating_offense} />
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
              Based on {ratings.history.length} self-rated session{ratings.history.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}

        {/* Pending ratings */}
        {pendingSessions.length > 0 && (
          <>
            <div className="section-header">
              <div className="section-title">Rate Your Sessions</div>
            </div>
            {pendingSessions.map(s => (
              <div key={s.id} className="card" style={{ padding: '14px 16px' }}>
                <div className="row-between">
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{s.location_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                      {new Date(s.session_date).toLocaleDateString('en-PK', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                  <button className="btn btn-sm" style={{ background: 'var(--accent-light)', color: 'var(--accent)', border: '1px solid #bfdbfe' }}
                    onClick={() => openRatingModal(s)}>
                    Rate ⭐
                  </button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Rating history */}
        {ratings?.history.length > 0 && (
          <>
            <div className="section-header" style={{ marginTop: 8 }}>
              <div className="section-title">Rating History</div>
            </div>
            {ratings.history.map(r => (
              <div key={r.id} className="card" style={{ padding: '14px 16px' }}>
                <div className="row-between" style={{ marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{r.location_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                      {new Date(r.session_date).toLocaleDateString('en-PK', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent2)', fontSize: 12 }}
                    onClick={() => openRatingModal({ id: r.session_id, location_name: r.location_name, session_date: r.session_date })}>
                    Edit
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {r.errors_rating && <span style={{ fontSize: 12, color: 'var(--text2)' }}>Errors: {'⭐'.repeat(r.errors_rating)}</span>}
                  {r.defense_rating && <span style={{ fontSize: 12, color: 'var(--text2)' }}>Defense: {'⭐'.repeat(r.defense_rating)}</span>}
                  {r.offense_rating && <span style={{ fontSize: 12, color: 'var(--text2)' }}>Offense: {'⭐'.repeat(r.offense_rating)}</span>}
                  {r.session_enjoyment && <span style={{ fontSize: 12, color: 'var(--text2)' }}>Fun: {'⭐'.repeat(r.session_enjoyment)}</span>}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Rating modal */}
      {ratingModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setRatingModal(null)}>
          <div className="modal">
            <div className="modal-handle" />
            <div style={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, fontSize: 18, marginBottom: 4 }}>Rate Your Performance</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>{ratingModal.location_name} · {new Date(ratingModal.session_date).toLocaleDateString('en-PK', { month: 'short', day: 'numeric' })}</div>

            <StarRating label="Unforced Errors (1 = many errors, 5 = clean game)" value={ratingForm.errorsRating} onChange={v => setRatingForm({ ...ratingForm, errorsRating: v })} />
            <StarRating label="Defense" value={ratingForm.defenseRating} onChange={v => setRatingForm({ ...ratingForm, defenseRating: v })} />
            <StarRating label="Offense" value={ratingForm.offenseRating} onChange={v => setRatingForm({ ...ratingForm, offenseRating: v })} />
            <StarRating label="Session Enjoyment 🎾" value={ratingForm.sessionEnjoyment} onChange={v => setRatingForm({ ...ratingForm, sessionEnjoyment: v })} />

            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setRatingModal(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 2 }} onClick={submitRating} disabled={submitting}>
                {submitting ? 'Saving...' : 'Save Rating'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
